/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * The event wizard's whole form model, as a pure reducer.
 *
 * Kept free of React so the shape can be tested directly, and so an Edit screen
 * can hydrate the same state from an existing record and reuse every step
 * component unchanged.
 */

export const emptyRoute = () => ({
  start_point: { lat: '', lng: '' },
  start_address: '',
  end_point: { lat: '', lng: '' },
  end_address: '',
  total_distance: '',
  distance_measuring_unit_id: '',
  gpx_file_url: '',
});

export const emptyVenue = () => ({
  venue_name: '',
  venue_address: '',
  venue_point: { lat: '', lng: '' },
});

export const emptyActivity = () => ({
  name: '',
  description: '',
  activity_type_id: '',
  location_model: 'ROUTE_BASED',
  route: emptyRoute(),
  venue: null,
  pit_stops: [],
});

export const emptySponsor = () => ({
  name: '', logo_url: '', cover_image: '', website_url: '',
});

export const emptyPitStop = () => ({
  name: '', description: '', point: { lat: '', lng: '' },
});

export const emptyPlatformRegistration = () => ({
  registration_start_datetime: '',
  registration_end_datetime: '',
  max_participants: '',
});

export const emptyExternalRegistration = () => ({
  external_url: '',
  registration_start_date: '',
  registration_end_date: '',
});

export const initialEventForm = () => ({
  title: '',
  slug: '',
  description: '',
  prerequisites: '',
  preparation_steps: '',
  cover_image_url: '',
  official_website_url: '',
  creator_type: 'ORGANIZATION',
  creator_organization_id: '',
  creator_user_id: '',
  organizer: 'PLATFORM',
  activity_mode: 'DEFAULT',
  location: '',
  location_state: '',
  location_country: '',
  location_pincode: '',
  timezone: '',
  start_datetime: '',
  end_datetime: '',
  registration_source: 'PLATFORM',
  registration_type_id: '',
  is_registration_required: true,
  // Payments are deferred this phase; the flag stays false and the fee is
  // pinned to zero when the batch is built.
  is_payment_required: false,
  status: 'DRAFT',
  activities: [],
  sponsors: [],
  platform_registration: emptyPlatformRegistration(),
  external_registration: null,
});

/** Replaces one entry of a list without mutating the original. */
function replaceAt(list, index, updater) {
  return list.map((entry, position) => (position === index ? updater(entry) : entry));
}

/**
 * Registration groups follow the source. Holding on to a window the event no
 * longer uses would submit contradictory rows.
 *
 * D-54: `registration_source` says only WHERE a signup is taken — PLATFORM or
 * EXTERNAL. Whether one is taken at all is `is_registration_required`, and what
 * kind it is belongs to `registration_type_id`. The trailing branch is a guard
 * for an unset value, not a third source.
 */
function applyRegistrationSource(state, source) {
  if (source === 'PLATFORM') {
    return {
      ...state,
      registration_source: source,
      platform_registration: state.platform_registration || emptyPlatformRegistration(),
      external_registration: null,
    };
  }
  if (source === 'EXTERNAL') {
    return {
      ...state,
      registration_source: source,
      platform_registration: null,
      external_registration: state.external_registration || emptyExternalRegistration(),
    };
  }
  return {
    ...state,
    registration_source: source,
    platform_registration: null,
    external_registration: null,
  };
}

/**
 * An activity is route-based or venue-based, never both. Clearing the unused
 * side keeps the batch from carrying a half-filled child row.
 */
function applyLocationModel(activity, model) {
  if (model === 'ROUTE_BASED') {
    return { ...activity, location_model: model, route: activity.route || emptyRoute(), venue: null };
  }
  return {
    ...activity,
    location_model: model,
    route: null,
    venue: activity.venue || emptyVenue(),
    pit_stops: [],
  };
}

export function eventFormReducer(state, action) {
  switch (action.type) {
    case 'setField':
      if (action.field === 'registration_source') {
        return applyRegistrationSource(state, action.value);
      }
      // chk_event_creator_xor allows only one creator column, so switching
      // author type clears the side that is no longer in use.
      if (action.field === 'creator_type') {
        return action.value === 'USER'
          ? { ...state, creator_type: action.value, creator_organization_id: '' }
          : { ...state, creator_type: action.value, creator_user_id: '' };
      }
      return { ...state, [action.field]: action.value };

    case 'updateNested':
      return {
        ...state,
        [action.group]: { ...(state[action.group] || {}), [action.field]: action.value },
      };

    case 'addActivity':
      return { ...state, activities: [...state.activities, emptyActivity()] };

    case 'removeActivity':
      return {
        ...state,
        activities: state.activities.filter((_, position) => position !== action.index),
      };

    case 'updateActivity':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => (
          action.field === 'location_model'
            ? applyLocationModel(activity, action.value)
            : { ...activity, [action.field]: action.value }
        )),
      };

    case 'updateActivityNested':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          [action.group]: { ...(activity[action.group] || {}), [action.field]: action.value },
        })),
      };

    case 'updateActivityPoint':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          [action.group]: {
            ...(activity[action.group] || {}),
            [action.field]: { ...(activity[action.group]?.[action.field] || {}), [action.axis]: action.value },
          },
        })),
      };

    case 'addPitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: [...(activity.pit_stops || []), emptyPitStop()],
        })),
      };

    case 'removePitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: (activity.pit_stops || []).filter((_, position) => position !== action.stopIndex),
        })),
      };

    case 'updatePitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: replaceAt(activity.pit_stops || [], action.stopIndex, (stop) => ({
            ...stop, [action.field]: action.value,
          })),
        })),
      };

    case 'updatePitStopPoint':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: replaceAt(activity.pit_stops || [], action.stopIndex, (stop) => ({
            ...stop, point: { ...stop.point, [action.axis]: action.value },
          })),
        })),
      };

    case 'addSponsor':
      return { ...state, sponsors: [...state.sponsors, emptySponsor()] };

    case 'removeSponsor':
      return {
        ...state,
        sponsors: state.sponsors.filter((_, position) => position !== action.index),
      };

    case 'updateSponsor':
      return {
        ...state,
        sponsors: replaceAt(state.sponsors, action.index, (sponsor) => ({
          ...sponsor, [action.field]: action.value,
        })),
      };

    case 'hydrate':
      return { ...initialEventForm(), ...action.value };

    default:
      return state;
  }
}
